import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useRecoilState } from 'recoil';
import { SelectedGroupsAtomState } from '../../../recoil/ModalAtom';
import {
  splitGroupsBySection,
  SECTION_META,
  SECTION_CUSTOM,
  SECTION_TEMPLATE,
} from '../../utils/groupSection';

const REQUIRED_GROUP_KEY = 'infraSetting';

const SidebarWrap = styled.aside`
  width: 260px;
  flex-shrink: 0;
  background: ${p => p.theme.bgSidebar};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  display: flex;
  flex-direction: column;
  height: fit-content;
  max-height: calc(100vh - 180px);
  position: sticky;
  top: 88px;
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    width: 100%;
    max-height: none;
    position: static;
    /* 모바일: 1단계 이후엔 사이드바 숨김 — 그룹 선택은 1단계에서 끝났음 */
    ${p => p.$hideOnMobileWhenAdvanced && `
      display: none;
    `}
  }
`;

const Header = styled.div`
  padding: 16px 18px 8px;

  @media screen and (max-width: 1024px) {
    padding: 10px 12px 6px;
  }
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.text};

  @media screen and (max-width: 1024px) {
    font-size: 13px;
  }
`;

const SubTitle = styled.p`
  margin: 0 0 12px;
  font-size: 12px;
  color: ${p => p.theme.textMuted};
  line-height: 1.5;

  @media screen and (max-width: 1024px) {
    margin: 0 0 8px;
    font-size: 11px;
    line-height: 1.4;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${p => p.theme.borderInput};
  border-radius: ${p => p.theme.radiusSm};
  background: ${p => p.theme.bgInput};
  color: ${p => p.theme.text};
  font-size: 13px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.borderInputFocus};
  }
`;

const TreeWrap = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 12px;
  font-size: 13px;

  @media screen and (max-width: 1024px) {
    padding: 6px 10px 8px;
    font-size: 12px;
  }
`;

const SectionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  cursor: pointer;
  user-select: none;
  font-weight: 700;
  color: ${p => p.theme.textSecondary};
  font-size: 12px;
  letter-spacing: 0.02em;
  margin-top: 4px;
`;

const GroupRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-left: 12px;
  border-radius: ${p => p.theme.radiusSm};
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  color: ${p => p.theme.text};
  user-select: none;
  font-size: 13px;
  line-height: 1.35;

  &:hover {
    background: ${p => p.$disabled ? 'transparent' : p.theme.bgHover};
  }

  @media screen and (max-width: 1024px) {
    padding: 4px 6px;
    margin-left: 8px;
    font-size: 12px;
    gap: 6px;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  accent-color: ${p => p.theme.accent};
  width: 14px;
  height: 14px;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  flex-shrink: 0;
`;

const Footer = styled.div`
  border-top: 1px solid ${p => p.theme.border};
  padding: 12px;
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
`;

function matchesSearch(text, query) {
  if (!query) return true;
  return (text || '').toLowerCase().includes(query.toLowerCase());
}

export default function LeftSidebar({ groupList, $hideOnMobileWhenAdvanced }) {
  const [selectedGroups, setSelectedGroups] = useRecoilState(SelectedGroupsAtomState);
  const [search, setSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (sectionKey) => {
    setCollapsedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };

  const toggleGroup = (groupKey) => {
    if (groupKey === REQUIRED_GROUP_KEY) return;
    setSelectedGroups(prev =>
      prev.includes(groupKey)
        ? prev.filter(g => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleReset = () => {
    setSelectedGroups([REQUIRED_GROUP_KEY]);
  };

  const filteredGroups = useMemo(() => {
    if (!search) return groupList;
    return groupList.filter(g =>
      matchesSearch(g.std_value, search) || matchesSearch(g.std_key, search)
    );
  }, [groupList, search]);

  const sections = useMemo(() => splitGroupsBySection(filteredGroups), [filteredGroups]);

  const renderGroup = (group) => {
    const groupKey = group.std_key;
    const isRequired = groupKey === REQUIRED_GROUP_KEY;
    const checked = selectedGroups.includes(groupKey);
    return (
      <GroupRow key={groupKey} $disabled={isRequired}>
        <Checkbox
          checked={checked}
          disabled={isRequired}
          onChange={() => toggleGroup(groupKey)}
        />
        <span>
          {group.std_value}
          {isRequired ? ' (기본 제공)' : ''}
        </span>
      </GroupRow>
    );
  };

  const renderSection = (sectionKey) => {
    const groups = sections[sectionKey];
    if (!groups || groups.length === 0) return null;
    const meta = SECTION_META[sectionKey];
    const collapsed = !!collapsedSections[sectionKey];
    return (
      <div key={sectionKey} style={{ marginBottom: 6 }}>
        <SectionRow onClick={() => toggleSection(sectionKey)}>
          {meta.title}
        </SectionRow>
        {!collapsed && groups.map(renderGroup)}
      </div>
    );
  };

  return (
    <SidebarWrap $hideOnMobileWhenAdvanced={$hideOnMobileWhenAdvanced}>
      <Header>
        <Title>그룹 선택</Title>
        <SubTitle>필요한 그룹을 선택하여<br/>견적에 포함하세요.</SubTitle>
        <SearchInput
          placeholder="그룹 검색..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Header>

      <TreeWrap>
        {renderSection(SECTION_CUSTOM)}
        {renderSection(SECTION_TEMPLATE)}
      </TreeWrap>

      <Footer>
        <ResetButton onClick={handleReset}>선택 초기화</ResetButton>
      </Footer>
    </SidebarWrap>
  );
}
